/** Context help for individual form fields — a muted "?" that opens a small
 *  popover with a short explanation and a "Learn more" link into the Help
 *  articles (new tab). Same interaction as the QuerySearch syntax-help icon.
 *
 *  Placement conventions:
 *  - TextField / select: `InputProps={{ endAdornment: <FieldHelp help={…} /> }}`
 *    (or wrap the control in a flex row and place it as a sibling);
 *  - Switch/Checkbox rows: `label={labelWithHelp('Enabled', HELP.enabled)}` —
 *    the trigger prevents the label's default so it never toggles the control.
 */
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, IconButton, Link, Popover, Typography } from '@mui/material';
import { useState, type MouseEvent, type ReactNode } from 'react';

import { helpStoryPath, helpStoryTitle } from '@/help/paths';
import type { HelpStoryId } from '@/help/registry.gen';

export interface FieldHelpEntry {
  /** Popover heading — usually the field label. */
  title: string;
  /** One-to-three sentence explanation of the field. */
  body: string;
  /** Help story backing the "Learn more" link (opens in a new tab). Optional:
   *  help text that arrives from the backend (a preflight action's `fields()`)
   *  has no story to point at, and still deserves the same popover rather than
   *  a permanent caption under every control. */
  story?: HelpStoryId;
}

export function FieldHelp({ help }: { help: FieldHelpEntry }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = (e: MouseEvent<HTMLElement>) => {
    // Inside a <label> (FormControlLabel, InputLabel) a plain click would
    // also activate the labelled control — suppress that.
    e.preventDefault();
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };
  return (
    <>
      <IconButton
        size="small"
        aria-label={`About: ${help.title}`}
        onClick={open}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          p: 0.25,
          alignSelf: 'center',
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1.5, maxWidth: 340 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {help.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: help.story ? 1 : 0 }}>
            {help.body}
          </Typography>
          {help.story && (
            <Link
              href={helpStoryPath(help.story)}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
            >
              Learn more: {helpStoryTitle(help.story)} ↗
            </Link>
          )}
        </Box>
      </Popover>
    </>
  );
}

/** Compose a FormControlLabel/InputLabel value that carries a help icon. */
export function labelWithHelp(label: ReactNode, help: FieldHelpEntry): ReactNode {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {label}
      <FieldHelp help={help} />
    </Box>
  );
}
